import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Alert, CircularProgress,
  FormControlLabel, Switch, Divider, Paper, Stack,
  IconButton, Tabs, Tab, Avatar, Chip, Tooltip,
  List, ListItem, ListItemAvatar, ListItemText,
  TextField, MenuItem, Autocomplete, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  WorkspacePremium as WPLeaderIcon,
  Star as ProjectLeaderIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  ExitToApp as LeaveIcon,
  Campaign as CampaignIcon,
  Save as SaveIcon,
  Description as DetailsIcon,
  PersonAdd as PersonAddIcon,
  SwapHoriz as TransferIcon,
  WarningAmber as WarningIcon,
  Shield as PrivacyIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useUserContext } from 'shared/contexts/UserContext';
import {useTheme} from '@mui/material/styles';
import { ROLE_LABELS, ROLE_RANK, WHITE_TEXT_ROLES, getRoleColors } from 'shared/constants/roleConstants';
import { PUBLICATION_STATUSES, PUBLICATION_TYPES } from '../publication/publicationConstants';
import JournalPicker from '../publication/JournalPicker';
import DocumentPrivacyPanel from './DocumentPrivacyPanel';


const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Turkish' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'pl', label: 'Polish' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ru', label: 'Russian' },
];

const STATUS_OPTIONS = [
  { value: 'preparing',    label: 'Preparing Application' },
  { value: 'submitted',    label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'shortlisted',  label: 'Shortlisted' },
  { value: 'awarded',      label: 'Awarded / Funded' },
  { value: 'rejected',     label: 'Rejected' },
  { value: 'withdrawn',    label: 'Withdrawn' },
];


// Tab indices shift depending on whether Settings tab is shown (coordinator only)
// Use these as keys for active-tab comparison, resolved at render time
const TAB_DETAILS   = 'details';
const TAB_SETTINGS  = 'settings';
const TAB_MEMBERS   = 'members';
const TAB_PRIVACY   = 'privacy';

const ProposalSettingsDialog = ({ open, onClose, consortium, proposal, onSuccess, initialTab }) => {
  // Publications have no funding call, duration, work packages or funding-application settings
  const isPublication = proposal?.document_type === 'publication';
  const docLabel = isPublication ? 'Publication' : 'Proposal';
  // On a publication the project leader is the corresponding author
  const leaderLabel = isPublication ? 'Corresponding Author' : 'Project Leader';
  const consortiumId = consortium?.id;
  const fcaId = proposal?.funding_application?.id ?? proposal?.funding_application;

  const { user: currentUser } = useUserContext();
  const currentUserId = String(currentUser?.id ?? '');

  const isCoordinator = (consortium?.memberships || []).some(
    m => String(m?.member?.id ?? '') === currentUserId && m?.roles?.includes('coordinator')
  );

  const [activeTab, setActiveTab] = useState(TAB_DETAILS);
  // opened from the writer's Private chip: straight to the Privacy tab
  useEffect(() => { if (open) setActiveTab(initialTab || TAB_DETAILS); }, [open, initialTab]);

  const theme = useTheme();
  // ── Settings tab state ────────────────────────────────────────────────────
  const [consortiumSettings, setConsortiumSettings] = useState({
    members_can_invite: false,
    members_can_add_remove: false,
  });

  const [fcaSettings, setFcaSettings] = useState({
    section_locking_enabled: false,
    wp_leaders_can_invite: true,
    wp_leaders_can_add_remove: true,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // ── Details tab state ─────────────────────────────────────────────────────
  const [detailFormData, setDetailFormData] = useState({
    acronym: '', title: '', abstract: '',
    language: 'en', status: 'preparing',
    duration_months: '', ethics_flag: false,
    publication_type: 'journal_article', target_journal: '', journal_style: null,
  });
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailSuccess, setDetailSuccess] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [initialCallId, setInitialCallId] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [callSearchQuery, setCallSearchQuery] = useState('');

  // ── Members tab state ─────────────────────────────────────────────────────
  const [collaborationId, setCollaborationId] = useState(null);
  const [memberRoles, setMemberRoles] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberError, setMemberError] = useState(null);

  // ── Transfer project leader state ─────────────────────────────────────────
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState(null);
  const [transferSuccess, setTransferSuccess] = useState(false);

  // Eagerly set collaborationId from prop; fall back to FCA fetch for non-coordinators
  useEffect(() => {
    const fromProp = consortium?.funding_application?.collaboration_id;
    if (fromProp) {
      setCollaborationId(fromProp);
      return;
    }
    if (!open || !fcaId) return;
    axiosService.get(`/consortium/funding-applications/${fcaId}/`)
      .then(res => { if (res.data.collaboration_id) setCollaborationId(res.data.collaboration_id); })
      .catch(() => {});
  }, [open, consortium, fcaId]);

  // Load settings on open (coordinator only)
  useEffect(() => {
    if (!open || !consortiumId || !isCoordinator) return;
    setError(null);
    setSuccess(false);

    const loadSettings = async () => {
      setLoading(true);
      try {
        const csResp = await axiosService.get(`/consortium/consortiums/${consortiumId}/settings/`);
        setConsortiumSettings({
          members_can_invite: csResp.data.members_can_invite ?? false,
          members_can_add_remove: csResp.data.members_can_add_remove ?? false,
        });

        const fca = consortium?.funding_application;
        if (fca) {
          setFcaSettings({
            section_locking_enabled: fca.section_locking_enabled ?? false,
            wp_leaders_can_invite: fca.wp_leaders_can_invite ?? true,
            wp_leaders_can_add_remove: fca.wp_leaders_can_add_remove ?? true,
          });
          if (fca.collaboration_id) setCollaborationId(fca.collaboration_id);
        } else if (fcaId) {
          const fcaResp = await axiosService.get(`/consortium/funding-applications/${fcaId}/`);
          setFcaSettings({
            section_locking_enabled: fcaResp.data.section_locking_enabled ?? false,
            wp_leaders_can_invite: fcaResp.data.wp_leaders_can_invite ?? true,
            wp_leaders_can_add_remove: fcaResp.data.wp_leaders_can_add_remove ?? true,
          });
          if (fcaResp.data.collaboration_id) setCollaborationId(fcaResp.data.collaboration_id);
        }
      } catch (err) {
        setError('Failed to load settings.');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [open, consortiumId, fcaId, consortium, isCoordinator]);

  // Populate details form from proposal prop
  useEffect(() => {
    if (!proposal) return;
    setDetailFormData({
      acronym:         proposal.acronym         || '',
      title:           proposal.title           || '',
      abstract:        proposal.abstract        || '',
      language:        proposal.language        || 'en',
      status:          proposal.status          || 'preparing',
      duration_months: proposal.duration_months || '',
      ethics_flag:     proposal.ethics_flag     || false,
      publication_type: proposal.publication_type || 'journal_article',
      target_journal:   proposal.target_journal   || '',
      journal_style:    null,
    });
    const callId = proposal.call || null;
    setInitialCallId(callId);
    setSelectedCall(null);
    setCallSearchQuery('');
    if (callId) {
      axiosService.get(`/funding/calls/${callId}/`)
        .then(res => setSelectedCall(res.data))
        .catch(() => {});
    }
  }, [proposal]);

  // Load initial call list when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoadingCalls(true);
    axiosService.get('/funding/calls/', { params: { page_size: 30 } })
      .then(res => setCalls(res?.data?.results || res?.data || []))
      .catch(() => setCalls([]))
      .finally(() => setLoadingCalls(false));
  }, [open]);

  // Debounced call search
  useEffect(() => {
    if (callSearchQuery.length < 2) {
      if (callSearchQuery === '') {
        setLoadingCalls(true);
        axiosService.get('/funding/calls/', { params: { page_size: 30 } })
          .then(res => setCalls(res?.data?.results || res?.data || []))
          .catch(() => setCalls([]))
          .finally(() => setLoadingCalls(false));
      }
      return;
    }
    const timer = setTimeout(() => {
      setLoadingCalls(true);
      axiosService.get('/funding/calls/', { params: { search: callSearchQuery, page_size: 20 } })
        .then(res => setCalls(res?.data?.results || res?.data || []))
        .catch(() => setCalls([]))
        .finally(() => setLoadingCalls(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [callSearchQuery]);

  const [rolesReady, setRolesReady] = useState(false);   // the roles decide who gets the full dialog
  const loadMemberRoles = useCallback(async ({ quiet = false } = {}) => {
    if (!consortiumId) return;
    if (!quiet) setLoadingMembers(true);
    setMemberError(null);
    try {
      const res = await axiosService.get('/consortium/member-roles/', {
        params: { consortium_membership__consortium: consortiumId, page_size: 200 },
      });
      setMemberRoles(res.data?.results || res.data || []);
    } catch (err) {
      setMemberError('Failed to load members.');
    } finally {
      setLoadingMembers(false);
      setRolesReady(true);
    }
  }, [consortiumId]);

  // fresh roles on every opening (shown at once when known from before; quietly refreshed on switching to Members)
  useEffect(() => {
    if (!open || !consortiumId) return;
    loadMemberRoles({ quiet: rolesReady });
  }, [open, consortiumId, loadMemberRoles]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && activeTab === TAB_MEMBERS) loadMemberRoles({ quiet: true });
  }, [open, activeTab, loadMemberRoles]);

  // ── Role helpers ──────────────────────────────────────────────────────────

  const ROLE_COLOR = getRoleColors(theme);

  // Build role map using only roles relevant to this proposal/collaboration
  // project_leader: must match this proposal's id
  // other roles: collaboration-scoped to this collab or consortium-wide (null collaboration)
  const proposalIdStr = proposal?.id ? String(proposal.id) : null;
  const relevantRoles = memberRoles.filter(r => {
    if (r.name === 'project_leader') return proposalIdStr && String(r.proposal_id) === proposalIdStr;
    if (collaborationId) return !r.collaboration || String(r.collaboration) === String(collaborationId);
    return !r.collaboration;
  });

  const roleByMembershipId = {};
  relevantRoles.forEach(r => {
    if (!r.consortium_membership) return;
    const key = String(r.consortium_membership);
    const existing = roleByMembershipId[key];
    const newRank = r.is_in !== false ? (ROLE_RANK[r.name] ?? 0) : -1;
    const existingRank = existing ? (existing.is_in !== false ? (ROLE_RANK[existing.name] ?? 0) : -1) : -2;
    if (newRank > existingRank) roleByMembershipId[key] = r;
  });

  // Proposal-scoped leave markers: is_in=false + proposal_id matching this proposal.
  // These override the active collab-scoped roles to show the member as inactive in THIS proposal.
  const leaveMarkerByMembershipId = {};
  if (proposalIdStr) {
    memberRoles.forEach(r => {
      if (r.proposal_id && String(r.proposal_id) === proposalIdStr && r.is_in === false) {
        leaveMarkerByMembershipId[String(r.consortium_membership)] = r;
      }
    });
  }

  const allMemberships = consortium?.memberships || [];
  const wpLeaderUserIds = new Set(
    (proposal?.work_packages || []).map(wp => String(wp.wp_leader)).filter(Boolean)
  );

  // Project leader is whoever has the project_leader role scoped to this specific proposal
  const projectLeaderRole = memberRoles.find(
    r => r.name === 'project_leader' && r.is_in !== false
      && (proposal?.id ? String(r.proposal_id) === String(proposal.id) : true)
  );
  // Resolve the member's user id from consortium memberships
  const projectLeaderMembershipId = projectLeaderRole ? String(projectLeaderRole.consortium_membership) : null;
  const projectLeaderMembership = allMemberships.find(m => String(m.id) === projectLeaderMembershipId);
  const projectLeaderId = projectLeaderMembership ? String(projectLeaderMembership.member?.id ?? '') : null;

  const isProjectLeader = projectLeaderId && projectLeaderId === currentUserId;

  // Users who have access to the full dialog (coordinator or project leader)
  const hasFullAccess = isCoordinator || isProjectLeader;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await axiosService.patch(`/consortium/consortiums/${consortiumId}/settings/`, consortiumSettings);
      if (fcaId) {
        await axiosService.patch(`/consortium/funding-applications/${fcaId}/`, fcaSettings);
      }
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onSuccess?.();
        onClose();
      }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleDetailsSave = async () => {
    setDetailSaving(true);
    setDetailError(null);
    try {
      const newCallId = selectedCall?.id || null;
      const callChanged = newCallId !== initialCallId;

      const common = {
        acronym:     detailFormData.acronym,
        title:       detailFormData.title,
        abstract:    detailFormData.abstract,
        language:    detailFormData.language,
        status:      detailFormData.status,
        ethics_flag: detailFormData.ethics_flag,
      };
      const res = await axiosService.patch(`/proposal/proposals/${proposal.id}/`, isPublication
        ? {
            ...common,
            publication_type: detailFormData.publication_type,
            target_journal:   detailFormData.target_journal,
            // Picking a journal from the list switches the reference style
            ...(detailFormData.journal_style && {
              citation_style:       detailFormData.journal_style.id,
              citation_style_title: detailFormData.journal_style.title,
            }),
          }
        : {
            ...common,
            duration_months: detailFormData.duration_months ? parseInt(detailFormData.duration_months) : null,
            ...(callChanged && { call: newCallId }),
          });

      if (!isPublication && callChanged && proposal.funding_application) {
        const currentFcaId = proposal.funding_application?.id ?? proposal.funding_application;
        await axiosService.patch(`/consortium/funding-applications/${currentFcaId}/`, { call: newCallId });
      }

      setDetailSuccess(true);
      setTimeout(() => {
        setDetailSuccess(false);
        onSuccess?.(res.data);
      }, 1200);
    } catch (err) {
      setDetailError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        Object.values(err.response?.data || {}).flat().join(', ') ||
        `Failed to update ${docLabel.toLowerCase()} details.`
      );
    } finally {
      setDetailSaving(false);
    }
  };

  const handleLeaveClick = () => setConfirmLeaveOpen(true);

  const handleLeaveConfirm = async () => {
    setLeaving(true);
    setLeaveError(null);
    try {
      // you stay in the consortium; for a publication you also leave its author list
      await axiosService.post(`/proposal/proposals/${proposal?.id}/leave/`);
      setConfirmLeaveOpen(false);
      onClose();
      onSuccess?.('left');
    } catch (err) {
      // stays open with the reason (e.g. hand the corresponding author role over first)
      setLeaveError(err.response?.data?.error || err.response?.data?.detail || `Could not leave this ${docLabel.toLowerCase()}.`);
    } finally {
      setLeaving(false);
    }
  };

  const handleLeaveCancel = () => { setConfirmLeaveOpen(false); setLeaveError(null); };

  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState(null);
  const leaveLabel = `Leave ${docLabel.toLowerCase()}`;

  const [reinvitingId, setReinvitingId] = useState(null);
  const [reinviteSuccess, setReinviteSuccess] = useState(null);

  const handleReinvite = async (roleId) => {
    setReinvitingId(roleId);
    setMemberError(null);
    try {
      await axiosService.post(`/consortium/member-roles/${roleId}/reinvite/`);
      setReinviteSuccess(roleId);
      setTimeout(() => setReinviteSuccess(null), 2500);
    } catch (err) {
      setMemberError(
        err.response?.data?.error || err.response?.data?.detail || 'Failed to send invite.'
      );
    } finally {
      setReinvitingId(null);
    }
  };

  const handleTransferLeader = async () => {
    if (!transferTargetId || !proposal?.id) return;
    setTransferring(true);
    setTransferError(null);
    try {
      await axiosService.post('/consortium/member-roles/transfer_project_leader/', {
        proposal_id: proposal.id,
        consortium_membership_id: transferTargetId,
      });
      setTransferSuccess(true);
      setTransferTargetId('');
      await loadMemberRoles({ quiet: true });
      onSuccess?.();
      // the leader who handed the role over (and is not the coordinator) no longer manages the members: close
      setTimeout(() => { setTransferSuccess(false); if (!isCoordinator) onClose(); }, isCoordinator ? 2500 : 1500);
    } catch (err) {
      setTransferError(
        err.response?.data?.error || err.response?.data?.detail || 'Failed to transfer leadership.'
      );
    } finally {
      setTransferring(false);
    }
  };

  const setConsortiumField = (field) => (e) =>
    setConsortiumSettings(prev => ({ ...prev, [field]: e.target.checked }));

  const setFcaField = (field) => (e) =>
    setFcaSettings(prev => ({ ...prev, [field]: e.target.checked }));

  const setDetail = (field) => (e) =>
    setDetailFormData(prev => ({ ...prev, [field]: e.target.value }));

  // ── Tab renderers ─────────────────────────────────────────────────────────

  const renderSettingsTab = () => (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <PeopleIcon color="primary" sx={{ mr: 1 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Member Permissions</Typography>
            <Typography variant="caption" color="text.secondary">
              Consortium-wide — applies to all {isPublication ? 'publications and proposals' : 'proposals'}
            </Typography>
          </Box>
        </Box>
        <Stack spacing={0.5}>
          <FormControlLabel
            control={<Switch checked={consortiumSettings.members_can_invite} onChange={setConsortiumField('members_can_invite')} color="primary" size="small" />}
            label={<Box><Typography variant="body2">Invite Members</Typography><Typography variant="caption" color="text.secondary">Members can invite new people</Typography></Box>}
          />
          <FormControlLabel
            control={<Switch checked={consortiumSettings.members_can_add_remove} onChange={setConsortiumField('members_can_add_remove')} color="primary" size="small" />}
            label={<Box><Typography variant="body2">Add / Remove Members</Typography><Typography variant="caption" color="text.secondary">Members can add or remove others</Typography></Box>}
          />
        </Stack>
      </Paper>

      {!isPublication && (<>
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <WPLeaderIcon color="primary" sx={{ mr: 1 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>WP Leader Permissions</Typography>
            <Typography variant="caption" color="text.secondary">
              Proposal-level — applies to this proposal only
            </Typography>
          </Box>
        </Box>
        <Stack spacing={0.5}>
          <FormControlLabel
            control={<Switch checked={fcaSettings.wp_leaders_can_invite} onChange={setFcaField('wp_leaders_can_invite')} color="primary" size="small" />}
            label={<Box><Typography variant="body2">Invite Members</Typography><Typography variant="caption" color="text.secondary">WP leaders can invite new people</Typography></Box>}
          />
          <FormControlLabel
            control={<Switch checked={fcaSettings.wp_leaders_can_add_remove} onChange={setFcaField('wp_leaders_can_add_remove')} color="primary" size="small" />}
            label={<Box><Typography variant="body2">Add / Remove Members</Typography><Typography variant="caption" color="text.secondary">WP leaders can add or remove others</Typography></Box>}
          />
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          {fcaSettings.section_locking_enabled
            ? <LockIcon color="primary" sx={{ mr: 1 }} />
            : <EditIcon color="action" sx={{ mr: 1 }} />}
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Writing Workflow</Typography>
            <Typography variant="caption" color="text.secondary">
              Controls section editing and approval for this proposal
            </Typography>
          </Box>
        </Box>
        <FormControlLabel
          control={<Switch checked={fcaSettings.section_locking_enabled} onChange={setFcaField('section_locking_enabled')} color="primary" />}
          label={
            <Box>
              <Typography variant="body2">Enable Section Locking &amp; Approval</Typography>
              <Typography variant="caption" color="text.secondary">
                {fcaSettings.section_locking_enabled
                  ? 'Sections must be locked before editing. Writers submit for review; coordinators approve or push back.'
                  : 'Anyone can edit any section at any time — no locking or approval required (default).'}
              </Typography>
            </Box>
          }
        />
      </Paper>
      </>)}
    </Stack>
  );

  const renderDetailsTab = () => (
    <Stack spacing={2}>
      {detailError   && <Alert severity="error"   onClose={() => setDetailError(null)}>{detailError}</Alert>}
      {detailSuccess && <Alert severity="success">{docLabel} details saved!</Alert>}

      <Stack direction="row" spacing={2}>
        <TextField
          label="Acronym" required fullWidth
          value={detailFormData.acronym}
          onChange={setDetail('acronym')}
          disabled={detailSaving}
        />
        {!isPublication && (
          <TextField
            label="Duration (months)" type="number" fullWidth
            value={detailFormData.duration_months}
            onChange={setDetail('duration_months')}
            disabled={detailSaving}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        )}
        <TextField
          label="Status" select fullWidth
          value={detailFormData.status}
          onChange={setDetail('status')}
          disabled={detailSaving}
          SelectProps={{ MenuProps: { sx: { zIndex: 3001 } } }}
        >
          {(isPublication ? PUBLICATION_STATUSES : STATUS_OPTIONS).map(o => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <TextField
        label="Title" required fullWidth
        value={detailFormData.title}
        onChange={setDetail('title')}
        disabled={detailSaving}
      />

      <TextField
        label="Abstract" multiline rows={4} fullWidth
        value={detailFormData.abstract}
        onChange={setDetail('abstract')}
        disabled={detailSaving}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          label="AI Output Language" select fullWidth
          value={detailFormData.language}
          onChange={setDetail('language')}
          disabled={detailSaving}
          helperText="The AI writing assistant will produce text in this language"
          SelectProps={{ MenuProps: { sx: { zIndex: 3001 } } }}
        >
          {LANGUAGES.map(l => (
            <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Ethics Review Required" select fullWidth
          value={detailFormData.ethics_flag}
          onChange={(e) => setDetailFormData(prev => ({ ...prev, ethics_flag: e.target.value === true || e.target.value === 'true' }))}
          disabled={detailSaving}
          SelectProps={{ MenuProps: { sx: { zIndex: 3001 } } }}
        >
          <MenuItem value={false}>No</MenuItem>
          <MenuItem value={true}>Yes</MenuItem>
        </TextField>
      </Stack>

      {isPublication && (
        <Stack direction="row" spacing={2}>
          <TextField
            label="Publication Type" select sx={{ flex: 1, minWidth: 0 }}
            value={detailFormData.publication_type}
            onChange={setDetail('publication_type')}
            disabled={detailSaving}
            SelectProps={{ MenuProps: { sx: { zIndex: 3001 } } }}
          >
            {PUBLICATION_TYPES.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <JournalPicker
              label="Target Journal"
              value={{ title: detailFormData.target_journal, styleId: detailFormData.journal_style?.id || null }}
              onChange={(j) => setDetailFormData(prev => ({
                ...prev,
                target_journal: j.title,
                journal_style: j.styleId ? { id: j.styleId, title: j.styleTitle } : null,
              }))}
              helperText={detailFormData.journal_style ? 'References will switch to this journal’s style' : 'Pick from the list to use the journal’s reference style'}
            />
          </Box>
        </Stack>
      )}

      {!isPublication && (
      <Autocomplete
        value={selectedCall}
        onChange={(_, newValue) => setSelectedCall(newValue)}
        onInputChange={(_, newInput, reason) => {
          if (reason === 'input') setCallSearchQuery(newInput);
          else if (reason === 'clear') setCallSearchQuery('');
        }}
        options={calls}
        getOptionLabel={(option) => option?.title || option?.code || ''}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        filterOptions={(x) => x}
        loading={loadingCalls}
        disabled={detailSaving}
        slotProps={{ popper: { style: { zIndex: 3001 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Related Funding Call"
            placeholder="Search for funding calls…"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <>
                    <CampaignIcon sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {loadingCalls && <CircularProgress color="inherit" size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body2" fontWeight={500}>{option.code || 'No code'}</Typography>
              <Typography variant="caption" color="text.secondary">{option.title}</Typography>
            </Box>
          </li>
        )}
      />
      )}
    </Stack>
  );

  const renderMembersTab = () => {
    if (loadingMembers) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Memberships eligible to receive project leadership (active, not already project leader)
    const eligibleForLeader = allMemberships.filter(m => {
      const msId = String(m.id);
      const isCurrentLeader = msId === projectLeaderMembershipId;
      const role = roleByMembershipId[msId];
      const isActive = !role || role.is_in !== false;
      return !isCurrentLeader && isActive && m.member;
    });

    return (
      <Stack spacing={2}>
        {memberError && (
          <Alert severity="error" onClose={() => setMemberError(null)}>{memberError}</Alert>
        )}

        {/* Transfer project leadership */}
        {hasFullAccess && proposal?.id && (
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'secondary.main', bgcolor: 'secondary.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <ProjectLeaderIcon color="secondary" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{isPublication ? 'Change Corresponding Author' : 'Transfer Project Leadership'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {isPublication ? 'Current corresponding author' : 'Current leader'}: <strong>{projectLeaderMembership?.member?.name || '— none assigned —'}</strong>
                </Typography>
              </Box>
            </Box>
            {transferError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setTransferError(null)}>{transferError}</Alert>}
            {transferSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{isPublication ? 'Corresponding author changed.' : 'Project leadership transferred.'}</Alert>}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>New {leaderLabel}</InputLabel>
                <Select
                  value={transferTargetId}
                  label={`New ${leaderLabel}`}
                  onChange={e => setTransferTargetId(e.target.value)}
                  MenuProps={{ sx: { zIndex: 3100 } }}
                >
                  <MenuItem value=""><em>— Select member —</em></MenuItem>
                  {eligibleForLeader.map(m => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.member?.name || m.member?.email || 'Unknown'}
                      {m.organization?.short_name ? ` (${m.organization.short_name})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="secondary"
                startIcon={transferring ? <CircularProgress size={14} color="inherit"  /> : <TransferIcon sx={{backgroundColor: 'secondary'}} />}
                disabled={!transferTargetId || transferring}
                onClick={handleTransferLeader}
                sx={{ whiteSpace: 'nowrap', color: 'white', backgroundColor: 'secondary.main', '&:hover': { backgroundColor: 'secondary.dark' } }}
              >
                {transferring ? 'Transferring…' : 'Transfer'}
              </Button>
            </Stack>
          </Paper>
        )}

        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PeopleIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>Members</Typography>
            {!isPublication && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                · WP leaders are assigned through the work packages
              </Typography>
            )}
          </Box>

          {allMemberships.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No members found.</Typography>
          ) : (
            <List disablePadding>
              {allMemberships.map((membership) => {
                const msId = String(membership.id);
                const role = roleByMembershipId[msId];
                // Leave marker takes priority: if a proposal-scoped is_in=false exists, member
                // is considered inactive in THIS proposal even if their consortium role is active.
                const leaveMarker = leaveMarkerByMembershipId[msId];
                const member = membership.member;
                const name = member?.name || member?.username || 'Unknown';
                const isCoordinatorRole = role?.name === 'coordinator';
                const isWPLeader = wpLeaderUserIds.has(String(member?.id ?? ''));
                const isTaskLeader = !isCoordinatorRole && !isWPLeader && role?.name === 'task_leader';
                const isInactive = leaveMarker ? true : role?.is_in === false;
                const isInviteBlocked = leaveMarker ? leaveMarker.invite_blocked === true : role?.invite_blocked === true;
                const reinviteRoleId = leaveMarker?.id || role?.id;
                const isOwnRow = String(member?.id ?? '') === currentUserId;
                const isPL = projectLeaderMembershipId && msId === projectLeaderMembershipId;
                const canLeaveRow = !hasFullAccess && isOwnRow && !isInactive && !isCoordinatorRole && role;
                const canReinvite = hasFullAccess && isInactive && !isInviteBlocked && reinviteRoleId;
                const hasPadding = canLeaveRow || canReinvite;

                return (
                  <ListItem
                    key={msId}
                    disableGutters
                    secondaryAction={
                      canLeaveRow ? (
                        <Button size="small" color="error" startIcon={<LeaveIcon />}
                          onClick={() => handleLeaveClick(role.id)} variant="outlined">
                          Leave
                        </Button>
                      ) : canReinvite ? (
                        reinviteSuccess === reinviteRoleId ? (
                          <Typography variant="caption" color="success.main" sx={{ mr: 1 }}>Invite sent!</Typography>
                        ) : (
                          <Tooltip title="Send a re-invite notification to this member" slotProps={{ popper: { style: { zIndex: 3100 } } }}>
                            <Button size="small" color="primary" startIcon={
                              reinvitingId === reinviteRoleId ? <CircularProgress size={12} /> : <PersonAddIcon />
                            }
                              onClick={() => handleReinvite(reinviteRoleId)}
                              disabled={reinvitingId === reinviteRoleId}
                              variant="outlined">
                              Invite
                            </Button>
                          </Tooltip>
                        )
                      ) : null
                    }
                    sx={{
                      py: 0.75,
                      opacity: isInactive ? 0.5 : 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 },
                      pr: hasPadding ? 14 : 0,
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar src={member?.avatar} sx={{ width: 32, height: 32, fontSize: 13,
                            backgroundColor: theme.palette.primary.avatar,
                            color: 'white',
                       }}>
                        {name[0]?.toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={500}>{name}</Typography>
                          {isPL && (
                            <Chip label={leaderLabel} size="small" sx={{ backgroundColor: ROLE_COLOR.project_leader, color: '#fff' }} />
                          )}
                          {isCoordinatorRole && (
                            <Chip label="Coordinator" size="small" sx={{ backgroundColor: ROLE_COLOR.coordinator, color: '#fff' }} />
                          )}
                          {isWPLeader && (
                            <Chip label="WP Leader" size="small" sx={{ backgroundColor: ROLE_COLOR.wp_leader, color: '#fff' }} />
                          )}
                          {isTaskLeader && (
                            <Chip label="Task Leader" size="small" sx={{ backgroundColor: ROLE_COLOR.task_leader, color: '#fff' }} />
                          )}
                          {!isCoordinatorRole && !isWPLeader && !isPL && !isTaskLeader && !isInactive && (() => {
                            const rn = role?.name;
                            return (
                              <Chip
                                label={ROLE_LABELS[rn] || 'Researcher'}
                                size="small"
                                {...(ROLE_COLOR[rn]
                                  ? { sx: { backgroundColor: ROLE_COLOR[rn], color: WHITE_TEXT_ROLES.includes(rn) ? '#fff' : 'inherit' } }
                                  : { variant: 'outlined' }
                                )}
                              />
                            );
                          })()}
                          {isInactive && <Chip label="Inactive" size="small" />}
                        </Box>
                      }
                      secondary={member?.position || member?.email}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      </Stack>
    );
  };

  // ── Non-leader/coordinator view (read-only member list) ───────────────────
  // until the roles arrive it is unknown whether this member leads the document: no flash of the wrong view
  if (!hasFullAccess && !rolesReady) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}
        slotProps={{ paper: { sx: { borderRadius: 1 } } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Dialog>
    );
  }

  if (!hasFullAccess) {
    const myMembership = allMemberships.find(m => String(m?.member?.id ?? '') === currentUserId);
    const myLeaveMarker = myMembership ? leaveMarkerByMembershipId[String(myMembership.id)] : null;
    // anyone but the corresponding author / project leader (they hand the role over first)
    const canLeave = Boolean(myMembership) && !myLeaveMarker && !isProjectLeader;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 3000 }}
        slotProps={{ paper: { sx: { borderRadius: 1 } } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">Project Members</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {memberError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMemberError(null)}>{memberError}</Alert>
          )}

          {loadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List disablePadding>
              {allMemberships.map((membership) => {
                const msId = String(membership.id);
                const role = roleByMembershipId[msId];
                const member = membership.member;
                const name = member?.name || member?.username || 'Unknown';
                const isOwn = String(member?.id ?? '') === currentUserId;
                const isActive = role?.is_in !== false;
                const isWPLeaderMember = wpLeaderUserIds.has(String(member?.id ?? ''));
                const isPL = projectLeaderMembershipId && msId === projectLeaderMembershipId;

                return (
                  <ListItem
                    key={msId}
                    disableGutters
                    sx={{
                      py: 0.75,
                      opacity: role?.is_in === false ? 0.5 : 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar src={member?.avatar} sx={{ width: 32, height: 32, fontSize: 13,
                            backgroundColor: theme.palette.primary.avatar,
                            color: 'white',
                       }}>
                        {name[0]?.toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>


                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={isOwn ? 600 : 400}>{name}</Typography>
                          {isOwn && <Chip label="You" size="small" color="primary" variant="outlined" />}
                          {isPL && (
                            <Chip label={leaderLabel} size="small" sx={{ backgroundColor: ROLE_COLOR.project_leader, color: '#fff' }} />
                          )}
                          {isWPLeaderMember && (
                            <Chip label="WP Leader" size="small" sx={{ backgroundColor: ROLE_COLOR.wp_leader, color: '#fff' }} />
                          )}
                          {role && isActive && !isWPLeaderMember && role.name !== 'coordinator' && !isPL && (
                            <Chip
                              label={ROLE_LABELS[role.name] || role.name}
                              size="small"
                              {...(ROLE_COLOR[role.name]
                                ? { sx: { backgroundColor: ROLE_COLOR[role.name], color: WHITE_TEXT_ROLES.includes(role.name) ? '#fff' : 'inherit' } }
                                : { variant: 'outlined' }
                              )}
                            />
                          )}
                          {role && isActive && role.name === 'coordinator' && (
                            <Chip label="Coordinator" size="small" sx={{ backgroundColor: ROLE_COLOR.coordinator, color: '#fff' }} />
                          )}
                          {role?.is_in === false && <Chip label="Inactive" size="small" />}
                        </Box>
                      }
                      secondary={member?.position || member?.email}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
          <Divider sx={{ my: 2 }} />
          <DocumentPrivacyPanel proposalId={proposal?.id} docLabel={docLabel} />
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>Close</Button>
          {canLeave && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<LeaveIcon />}
              onClick={handleLeaveClick}
              disabled={leaving}
            >
              {leaveLabel}
            </Button>
          )}
        </DialogActions>

        {/* Leave confirmation */}
        <Dialog open={confirmLeaveOpen} onClose={handleLeaveCancel} maxWidth="xs" fullWidth sx={{ zIndex: 3100 }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            {leaveLabel}?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Are you sure you want to leave{' '}
              <Box component="span" fontWeight={600}>
                {proposal?.acronym || proposal?.title || `this ${docLabel.toLowerCase()}`}
              </Box>
              ?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isPublication
                ? 'You will no longer be among its authors and lose access to it straight away. You stay in the consortium and keep its other publications and proposals. To come back, the coordinator or the corresponding author invites you again.'
                : 'You lose access to it straight away. You stay in the consortium and keep its other proposals and publications. To come back, the coordinator or the project leader invites you again.'}
            </Typography>
          </DialogContent>
          {leaveError && <Alert severity="error" sx={{ mx: 3, mb: 1 }}>{leaveError}</Alert>}
          <DialogActions>
            <Button onClick={handleLeaveCancel} disabled={leaving}>Cancel</Button>
            <Button onClick={handleLeaveConfirm} variant="contained" color="error"
              disabled={leaving} startIcon={leaving ? <CircularProgress size={16} color="inherit" /> : <LeaveIcon />}>
              {leaving ? 'Leaving…' : leaveLabel}
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    );
  }

  // ── Full view (coordinator or project leader) ─────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 3000 }}
      slotProps={{ paper: { sx: { borderRadius: 1 } } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">{docLabel} Settings</Typography>
          {isProjectLeader && !isCoordinator && (
            <Chip label={leaderLabel} size="small" sx={{ ml: 1, backgroundColor: ROLE_COLOR.project_leader, color: '#fff' }} />
          )}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving || detailSaving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab value={TAB_DETAILS}   label="Details"   icon={<DetailsIcon />}   iconPosition="start" />
          {isCoordinator && <Tab value={TAB_SETTINGS} label="Settings" icon={<SettingsIcon />} iconPosition="start" />}
          <Tab value={TAB_MEMBERS}   label="Members"   icon={<PeopleIcon />}    iconPosition="start" />
          <Tab value={TAB_PRIVACY}   label="Privacy"   icon={<PrivacyIcon />}   iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {activeTab === TAB_SETTINGS && error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {activeTab === TAB_SETTINGS && success  && <Alert severity="success" sx={{ mb: 2 }}>Settings saved!</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {activeTab === TAB_DETAILS   && renderDetailsTab()}
            {activeTab === TAB_SETTINGS  && isCoordinator && renderSettingsTab()}
            {activeTab === TAB_MEMBERS   && renderMembersTab()}
            {activeTab === TAB_PRIVACY   && <DocumentPrivacyPanel proposalId={proposal?.id} docLabel={docLabel} />}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!isProjectLeader && (
          <Button variant="outlined" color="error" startIcon={<LeaveIcon />} onClick={handleLeaveClick}
            disabled={leaving || saving || detailSaving} sx={{ mr: 'auto' }}>
            {leaveLabel}
          </Button>
        )}
        <Button onClick={onClose} disabled={saving || detailSaving}>Cancel</Button>

        {activeTab === TAB_SETTINGS && isCoordinator && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={16} /> : <SettingsIcon />}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        )}

        {activeTab === TAB_DETAILS && (
          <Button
            variant="contained"
            onClick={handleDetailsSave}
            disabled={detailSaving || !detailFormData.acronym.trim() || !detailFormData.title.trim()}
            startIcon={detailSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {detailSaving ? 'Saving…' : 'Save Details'}
          </Button>
        )}
      </DialogActions>

      {/* Leave confirmation */}
      <Dialog open={confirmLeaveOpen} onClose={handleLeaveCancel} maxWidth="xs" fullWidth sx={{ zIndex: 3100 }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {leaveLabel}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Are you sure you want to leave{' '}
            <Box component="span" fontWeight={600}>
              {proposal?.acronym || proposal?.title || `this ${docLabel.toLowerCase()}`}
            </Box>
            ?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isPublication
                ? 'You will no longer be among its authors and lose access to it straight away. You stay in the consortium and keep its other publications and proposals. To come back, the coordinator or the corresponding author invites you again.'
                : 'You lose access to it straight away. You stay in the consortium and keep its other proposals and publications. To come back, the coordinator or the project leader invites you again.'}
          </Typography>
        </DialogContent>
        {leaveError && <Alert severity="error" sx={{ mx: 3, mb: 1 }}>{leaveError}</Alert>}
        <DialogActions>
          <Button onClick={handleLeaveCancel} disabled={leaving}>Cancel</Button>
          <Button onClick={handleLeaveConfirm} variant="contained" color="error"
            disabled={leaving} startIcon={leaving ? <CircularProgress size={16} color="inherit" /> : <LeaveIcon />}>
            {leaving ? 'Leaving…' : leaveLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProposalSettingsDialog;
